/**
 * @fileoverview Modal component for viewing institution details
 * Uses DmsModal and DmsInfoCard/DmsInfoField for consistent display
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { intl, DmsButton, DmsModal, DmsPermissions, fetchData } from "dmsbasereact";
import { Box, Typography, Grid, Chip, useMediaQuery, useTheme } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import { DmsInfoCard } from "../../components/DmsDetailModal/DmsInfoCard";
import { DmsInfoField } from "../../components/DmsDetailModal/DmsInfoField";
import { formatCnpj } from "../../utils/formatters";
import { InstitutionDocuments } from "./InstitutionDocuments";
import { UsersTab } from "../InstitutionCentralPage/tabs/UsersTab";
import { Permissions } from "../../constants/Permissions";
import { getInstitutionCategoryLabel } from "../../constants/InstitutionType";

/**
 * Get status chip with appropriate color
 */
const getStatusChip = (status) => {
  if (!status) return <Chip label="-" size="small" />;

  const statusLower = status.toLowerCase();

  if (statusLower === "aprovado" || statusLower === "approved" || statusLower === "ativo" || statusLower === "active") {
    return <Chip label={status} color="success" size="small" />;
  }
  if (statusLower === "pendente" || statusLower === "pending") {
    return <Chip label={status} color="warning" size="small" />;
  }
  if (statusLower === "inativo" || statusLower === "inactive") {
    return <Chip label={status} color="error" size="small" />;
  }
  return <Chip label={status} color="default" size="small" />;
};

/**
 * Modal for viewing institution details
 */
export const InstitutionDetailModal = ({ open,
  onClose,
  institution = null,
  onEdit = null}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = useState([]);

  const fetchUsers = useCallback(() => {
    if (!institution?.pk) return;
    fetchData(`/admin/institutions/${institution.pk}/users`, "GET", true).then((response) => {
      const data = response?.data?.data;
      setUsers(Array.isArray(data) ? data : []);
    });
  }, [institution?.pk]);

  useEffect(() => {
    if (open && institution?.pk) {
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [open, institution?.pk, fetchUsers]);

  if (!institution) return null;

  const handleEdit = () => {
    if (onEdit) {
      onEdit(institution);
    }
  };

  return (
    <DmsModal
      isVisible={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DmsModal.Header
        title={intl.get("InstitutionDetails").d("Institution Details")}
        onClose={onClose}
      />
      <DmsModal.Body>
        <Box sx={{ p: isMobile ? 1 : 2, maxHeight: "60vh", overflowY: "auto" }}>
          {/* Basic Information */}
          <DmsInfoCard icon={<BusinessIcon />} title={intl.get("BasicInfo").d("Basic Information")}>
            <Grid container spacing={2}>
              <DmsInfoField label={intl.get("Name").d("Name")} value={institution.name} xs={12} sm={6} />
              <DmsInfoField label={intl.get("CNPJ").d("CNPJ")} value={formatCnpj(institution.cnpj)} xs={12} sm={6} />
              <DmsInfoField
                label={intl.get("Category").d("Category")}
                value={
                  institution.categories && institution.categories.length > 0
                    ? institution.categories.map(c => getInstitutionCategoryLabel(c)).join(", ")
                    : getInstitutionCategoryLabel(institution.category)
                }
                xs={12} sm={6}
              />
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {intl.get("Status").d("Status")}
                </Typography>
                {getStatusChip(institution.status)}
              </Grid>
              <DmsInfoField
                label={intl.get("RegisteredAt").d("Registered at")}
                value={institution.created_at ? new Date(institution.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                xs={12}
                sm={6}
              />
              <DmsInfoField
                label={intl.get("LastUpdated").d("Last Updated")}
                value={institution.updated_on ? new Date(institution.updated_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                xs={12}
                sm={6}
              />
            </Grid>
          </DmsInfoCard>

          {/* Address Information */}
          {(institution.city || institution.state || institution.street) && (
            <DmsInfoCard icon={<LocationOnIcon />} title={intl.get("AddressInfo").d("Address Information")}>
              <Grid container spacing={2}>
                {institution.street && (
                  <DmsInfoField label={intl.get("Street").d("Street")} value={institution.street} xs={12} />
                )}
                {institution.neighborhood && (
                  <DmsInfoField label={intl.get("Neighborhood").d("Neighborhood")} value={institution.neighborhood} xs={12} sm={6} />
                )}
                {institution.postal_code && (
                  <DmsInfoField label={intl.get("ZipCode").d("Zip Code")} value={institution.postal_code} xs={12} sm={6} />
                )}
                <DmsInfoField label={intl.get("City").d("City")} value={institution.city} xs={12} sm={6} />
                <DmsInfoField label={intl.get("State").d("State")} value={institution.state} xs={12} sm={6} />
              </Grid>
            </DmsInfoCard>
          )}

          {/* Documents */}
          {institution.pk && (
            <InstitutionDocuments institutionPk={institution.pk} />
          )}

          {/* Linked Users */}
          {institution.pk && (
            <DmsInfoCard icon={<PeopleIcon />} title={intl.get("LinkedUsers").d("Linked Users")}>
              <UsersTab users={users} onRefresh={fetchUsers} />
            </DmsInfoCard>
          )}
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            width: "100%",
            p: isMobile ? 1 : 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <DmsButton
            variant="secondary"
            text={intl.get("Close").d("Close")}
            onClick={onClose}
            fullWidth={isMobile}
          />
          {onEdit && (
            <DmsPermissions permissions={Permissions.INSTITUTION_MANAGEMENT_EDIT}>
              <DmsButton
                variant="primary"
                text={intl.get("Edit").d("Edit")}
                icon={<EditIcon />}
                onClick={handleEdit}
                fullWidth={isMobile}
              />
            </DmsPermissions>
          )}
        </Box>
      </DmsModal.Footer>
    </DmsModal>
  );
};

InstitutionDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  institution: PropTypes.object,
  onEdit: PropTypes.func,
};

export default InstitutionDetailModal;
